import { SplashScreen } from "@capacitor/splash-screen";
import "bulma-slider/dist/css/bulma-slider.min.css";
import "bulma-switch/dist/css/bulma-switch.min.css";
import "bulma/css/bulma.min.css";
import "material-symbols/sharp.css";
import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import van from "vanjs-core";
import { AdminPage } from "./admin/admin_page";
import { Api } from "./api";
import { refreshToken } from "./api/token";
import { config, initAppEnv } from "./app-env/common";
import "./css/style.css";
import { registerDevicePage } from "./device/add_device_page";
import { devicesPage, onLeaveDevicesPage } from "./device/devices_page";
import { loadConnectionErrorPage } from "./error";
import { errorSection } from "./error-section";
import { registerFarmcamSimPage } from "./farmcam-sim/add_fcs_page";
import { AdminFcsPage } from "./farmcam-sim/fcs_admin_pages";
import { farmcamSimLogsPage } from "./farmcam-sim/fcs_logs_page";
import { loadFarmcamSimSettingsPage } from "./farmcam-sim/fcs_settings_page";
import { adminFaPage } from "./fencealarm/fa_admin_pages";
import { fencealarmLogsPage } from "./fencealarm/fa_logs_page";
import { fencealarmSettingsPage } from "./fencealarm/fa_settings_page";
import { registerFencealarmPage } from "./fencealarm/register_fa_page";
import { adminFa2Page } from "./fencealarm2/fa2_admin_pages";
import { fencealarm2LogsPage } from "./fencealarm2/fa2_logs_page";
import { fencealarm2SettingsPage } from "./fencealarm2/fa2_settings_page";
import { registerFencealarm2Page } from "./fencealarm2/register_fa2_page";
import { loadingScreen } from "./loading-screen";
import {
	getInitialNotification,
	MobileFlags,
	onAndroid,
	onMobile,
	startNotificationListeners,
	startStateListener,
} from "./mobile";
import { loadNoticesPage } from "./notices/notices_admin_page";
import { noticesSection } from "./notices/notices_section";
import { Router } from "./router";
import { registerSmartplugPage } from "./smartplug/register_sp_page";
import { AdminSpPage } from "./smartplug/sp_admin_pages";
import { smartplugLogsPage } from "./smartplug/sp_logs_page";
import { smartplugSettingsPage } from "./smartplug/sp_settings_page";
import { Sp2AdminPage } from "./smartplug2/sp2_admin_page";
import { Sp2FactoryResetPage } from "./smartplug2/sp2_factory_reset_page";
import { Sp2FactoryPage } from "./smartplug2/sp2_factory_tool_page";
import { Sp2FotaPage } from "./smartplug2/sp2_fota_page";
import { Sp2LogsPage } from "./smartplug2/sp2_logs_page";
import { registerSmartplug2Page } from "./smartplug2/sp2_registration_page";
import { Sp2SettingsPage } from "./smartplug2/sp2_settings_page";
import { state } from "./state";
import { AccountPage } from "./user/account_page";
import { DeleteAccountPage } from "./user/confirm_account_deletion_page";
import { PurchaseSmsPage } from "./user/confirm_sms_purchase_page";
import { createAccountPage } from "./user/create_account";
import { resetPasswordPage } from "./user/reset_password_page";
import { signInPage } from "./user/sign_in_page";
import {
	adminFindUserPage,
	adminInspectUserPage,
} from "./user/user_admin_pages";
import { verifyEmailPage } from "./user/verify_email_page";
import { delay } from "./utils";
import { Bulma } from "./view/bulma";
import { navbar } from "./view/navbar";
import { notFoundPage } from "./view/not_found_page";
import { redirectPage } from "./view/redirect_page";

initAppEnv();

const appContainer = document.createElement("div");
appContainer.className = "app-container";
if (onAndroid) appContainer.classList.add("is-android");

const modal = Bulma.modalCard();

van.add(
	document.body,
	appContainer,
	errorSection.ChildDom,
	loadingScreen.ChildDom,
	modal.element,
);

const router = new Router({
	notFoundHandler: notFoundPage,
	mountRoute: setPageContent,
	rootPath: "/",
});

router.routeContext = {
	api: new Api(config.apiHost, () => router.jump("/connection-error")),
	router: router,
	sp2gw: new Api(config.sp2gwHost, () => router.jump("/connection-error")),
};

router.registerRoute("/account", AccountPage);
router.registerRoute("/account/buy-sms", PurchaseSmsPage);
router.registerRoute("/account/delete", DeleteAccountPage);
router.registerRoute("/admin", AdminPage);
router.registerRoute("/admin/farmcam-sim/:imsi", AdminFcsPage);
router.registerRouteDEPRECATED("/admin/fencealarm/:imei", handler(adminFaPage));
router.registerRouteDEPRECATED(
	"/admin/fencealarm2/:imei",
	handler(adminFa2Page),
);
router.registerRouteDEPRECATED("/admin/notices", loadNoticesPage);
router.registerRoute("/admin/smartplug/:imei", AdminSpPage);
router.registerRoute("/admin/smartplug2/:imei", Sp2AdminPage);
router.registerRoute(
	"/admin/smartplug2/:imei/factory-reset",
	Sp2FactoryResetPage,
);
router.registerRoute("/admin/smartplug2/:imei/fota", Sp2FotaPage);
router.registerRouteDEPRECATED("/admin/user", handler(adminFindUserPage));
router.registerRouteDEPRECATED(
	"/admin/user/:id",
	handler(adminInspectUserPage),
);
router.registerRouteDEPRECATED("/connection-error", loadConnectionErrorPage);
router.registerRouteDEPRECATED("/create-account", createAccountPage);
router.registerRouteDEPRECATED("/devices", devicesPage, {
	leave: onLeaveDevicesPage,
});
router.registerRouteDEPRECATED("/devices/register", registerDevicePage);
router.registerRouteDEPRECATED(
	"/devices/register/farmcam-sim",
	registerFarmcamSimPage,
);
router.registerRouteDEPRECATED(
	"/devices/register/fencealarm",
	registerFencealarmPage,
);
router.registerRouteDEPRECATED(
	"/devices/register/fencealarm2",
	registerFencealarm2Page,
);
router.registerRouteDEPRECATED(
	"/devices/register/smartplug",
	registerSmartplugPage,
);
router.registerRouteDEPRECATED(
	"/devices/register/smartplug2",
	registerSmartplug2Page,
);
router.registerRoute("/factory/smartplug2", Sp2FactoryPage);
router.registerRouteDEPRECATED(
	"/farmcam-sim/:imsi",
	loadFarmcamSimSettingsPage,
);
router.registerRouteDEPRECATED("/farmcam-sim/:imsi/logs", farmcamSimLogsPage);
router.registerRouteDEPRECATED("/fencealarm/:imei", fencealarmSettingsPage);
router.registerRouteDEPRECATED("/fencealarm/:imei/logs", fencealarmLogsPage);
router.registerRouteDEPRECATED("/fencealarm2/:imei", fencealarm2SettingsPage);
router.registerRouteDEPRECATED("/fencealarm2/:imei/logs", fencealarm2LogsPage);
router.registerRouteDEPRECATED("/redirect", redirectPage.mount, {
	leave: redirectPage.onUnmount,
});
router.registerRouteDEPRECATED("/reset-password", resetPasswordPage);
router.registerRouteDEPRECATED("/sign-in", signInPage);
router.registerRouteDEPRECATED("/smartplug/:imei", smartplugSettingsPage);
router.registerRouteDEPRECATED("/smartplug/:imei/logs", smartplugLogsPage);
router.registerRoute("/smartplug2/:imei", Sp2SettingsPage);
router.registerRoute("/smartplug2/:imei/logs", Sp2LogsPage);
router.registerRouteDEPRECATED("/verify-account", verifyEmailPage);

loadingScreen.waitFor(init());

async function init(): Promise<void> {
	await delay(600);
	SplashScreen.hide();

	await state.loadUserLocale();
	await startStateListener();
	if (onMobile) {
		startNotificationListeners();

		// Wait for the app to receive the initial notification which is needed
		// before navigating to devices.
		if (!MobileFlags.getValue("appFinishedLoading")) {
			await delay(500);
		}
	}

	MobileFlags.setValue("appFinishedLoading", true);

	if (await state.isSignedIn()) {
		await initAuthenticated();
	} else {
		initUnauthenticated();
	}

	// Use delay to keep loading screen active during router.resolve()
	router.resolve();
	await delay(2000);
}

async function initAuthenticated(): Promise<void> {
	await refreshToken()
		.then(state.refreshDevices)
		.catch(() => {
			window.history.pushState(null, "", "/connection-error");
		});

	switch (window.location.pathname) {
		case "/":
		case "/create-account":
		case "/devices":
		case "/reset-password":
		case "/sign-in":
		case "/verify-account": {
			const imei = getInitialNotification()?.data?.Imei;
			const user = await state.getSignedInUser();
			if (imei) {
				window.history.replaceState(null, "", `/devices?id=${imei}`);
			} else if (user.AccountType === "smartplug2-factory") {
				window.history.replaceState(null, "", "/factory/smartplug2");
			} else {
				window.history.replaceState(null, "", "/devices");
			}
		}
	}
}

function initUnauthenticated(): void {
	switch (window.location.pathname) {
		case "/create-account":
		case "/reset-password":
		case "/sign-in":
		case "/smartplug2-provisioning":
		case "/verify-account":
			break;
		default:
			window.history.replaceState(null, "", "/sign-in");
	}
}

function handler(getRoot: (match: Match) => Promise<HTMLElement>) {
	return (match: Match | undefined): void => {
		if (!match) return;
		getRoot(match).then((root) => setPageContent(root));
	};
}

function setPageContent(childDom: ChildDom): void {
	const mountPageContent = async (childDom: ChildDom): Promise<void> => {
		appContainer.replaceChildren(
			await navbar(config.navbarTheme),
			await noticesSection(),
			Bulma.hero(childDom as Node),
		);
	};

	loadingScreen.waitFor(mountPageContent(childDom));
}

export { appContainer, modal, router, setPageContent };
